<template>
  <div class="audience-preview-section">
    <div class="flex flex-column mb-2">
      <h4 class="text-sm font-semibold text-blue-800 mb-2">🎯 Audience Preview</h4>
      <button
        :disabled="checkingTargetCount"
        @click="checkTargetedUsersCount"
        class="custom-button"
        style="border: 2px solid #3b82f6; color: #3b82f6; background: transparent; padding: 0.5rem 1rem; border-radius: 0.5rem; cursor: pointer; font-size: 0.875rem; transition: all 0.2s ease;"
      >
        <i v-if="checkingTargetCount" class="fa fa-spinner fa-spin mr-2"></i>
        <i v-else class="fa fa-users mr-2"></i>
        {{ checkingTargetCount ? 'Checking...' : 'Check audience size' }}
      </button>
    </div>
    
    <!-- Audience size result display -->
    <div v-if="audienceResult" class="audience-result mt-3">
      <div class="result-header">
        <i class="fa fa-check-circle mr-2 text-green-600"></i>
        <span class="font-semibold text-green-700">Audience Size Result</span>
      </div>
      <div class="result-content mt-2">
        <div class="stat-item">
          <span class="stat-label">Target Users: </span>
          <span class="stat-value">{{ audienceResult.targetedUsersCount.toLocaleString() }}</span>
        </div>
        <div class="stat-item">
          <span class="stat-label">User Type: </span>
          <span class="stat-value">{{ formatUserType(context.targetUserType) }}</span>
        </div>
        <div v-if="context.targetUserType === 1" class="stat-item">
          <span class="stat-label">Level Range: </span>
          <span class="stat-value">{{ context.targetLevelRange?.[0] || 1 }}-{{ context.targetLevelRange?.[1] || 4 }}</span>
        </div>
        <div class="stat-item">
          <span class="stat-label">Points Range: </span>
          <span class="stat-value">{{ formatPointsRange(context.targetPointsRange) }}</span>
        </div>
        <div v-if="context.targetMemberCardIds?.length > 0" class="stat-item">
          <span class="stat-label">Member Cards: </span>
          <span class="stat-value">{{ context.targetMemberCardIds.length }} selected</span>
        </div>
      </div>
    </div>
    
    <!-- Error display -->
    <div v-if="audienceError" class="audience-error mt-3">
      <div class="error-header">
        <i class="fa fa-exclamation-triangle mr-2 text-red-600"></i>
        <span class="font-semibold text-red-700">Error</span>
      </div>
      <p class="error-message mt-1">{{ audienceError }}</p>
    </div>
    
    <p v-if="!audienceResult && !audienceError" class="text-xs text-blue-600">
      Click the button above to see how many users your notification will target based on current settings.
    </p>
  </div>
</template>

<script>
import { ref } from "vue";
import { useStore } from 'vuex';

export default {
  components: {},
  props: {
    context: {
      type: Object,
      required: true
    },
    field: {
      type: Object,
      required: true
    }
  },
  setup(props) {
    const store = useStore();
    const checkingTargetCount = ref(false);
    const audienceResult = ref(null);
    const audienceError = ref(null);

    const checkTargetedUsersCount = async () => {
      try {
        checkingTargetCount.value = true;
        audienceResult.value = null;
        audienceError.value = null;
        
        // Map form context to API format - same as in the sidebars
        const targetingRequest = {
          targetUserType: props.context.targetUserType || 0,
          targetMinimumLevel: props.context.targetLevelRange?.[0] || 1,
          targetMaximumLevel: props.context.targetLevelRange?.[1] || 4,
          targetMinimumPointBalance: props.context.targetPointsRange?.[0] || 0,
          targetMaximumPointBalance: props.context.targetPointsRange?.[1] || 200000,
          targetMemberCardIds: props.context.targetMemberCardIds || [],
          targetMemberCardStatuses: (props.context.targetMemberCardStatuses || []).map(status => {
            switch (status) {
              case 0: return 'Active';
              case 1: return 'WaitingForConsent';
              case 2: return 'Expired';
              default: return 'Active';
            }
          })
        };

        const response = await store.dispatch('recurringNotifications/testTargetingCount', targetingRequest);
        audienceResult.value = response;
      } catch (error) {
        console.error('Error getting targeted users count:', error);
        audienceError.value = "Failed to get targeted users count. Please try again.";
      } finally {
        checkingTargetCount.value = false;
      }
    };

    const formatUserType = (userType) => {
      return userType === 1 ? 'Paid Users' : 'Free Users';
    };

    const formatPointsRange = (pointsRange) => {
      if (!pointsRange || pointsRange.length < 2) return '0-200,000';
      return `${pointsRange[0].toLocaleString()}-${pointsRange[1].toLocaleString()}`;
    };

    return {
      checkTargetedUsersCount,
      checkingTargetCount,
      audienceResult,
      audienceError,
      formatUserType,
      formatPointsRange,
    };
  },
};
</script>

<style scoped>
/* Audience preview section styling */
.audience-preview-section {
  background: linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%);
  border: 1px solid #bae6fd;
  border-radius: 0.75rem;
  padding: 1rem;
  margin: 1rem 0;
}

.audience-preview-section h4 {
  color: #0369a1;
  font-weight: 600;
  margin-bottom: 0.5rem;
}

.audience-preview-section p {
  color: #0c4a6e;
  font-size: 0.75rem;
  line-height: 1.4;
}

.custom-button:hover {
  background: #3b82f6 !important;
  color: white !important;
  transform: translateY(-1px);
  box-shadow: 0 4px 8px rgba(59, 130, 246, 0.3);
}

.custom-button:disabled {
  opacity: 0.6;
  cursor: not-allowed;
  transform: none;
}

.custom-button:disabled:hover {
  background: transparent !important;
  color: #3b82f6 !important;
  transform: none;
  box-shadow: none;
}

/* Audience result styling */
.audience-result {
  background: rgba(34, 197, 94, 0.1);
  border: 1px solid rgba(34, 197, 94, 0.3);
  border-radius: 0.5rem;
  padding: 0.75rem;
}

.result-header {
  display: flex;
  align-items: center;
  font-size: 0.875rem;
}

.result-content {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.stat-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 0.75rem;
}

.stat-label {
  color: #065f46;
  font-weight: 500;
}

.stat-value {
  color: #047857;
  font-weight: 600;
  background: rgba(34, 197, 94, 0.15);
  padding: 0.25rem 0.5rem;
  border-radius: 0.25rem;
}

/* Error styling */
.audience-error {
  background: rgba(239, 68, 68, 0.1);
  border: 1px solid rgba(239, 68, 68, 0.3);
  border-radius: 0.5rem;
  padding: 0.75rem;
}

.error-header {
  display: flex;
  align-items: center;
  font-size: 0.875rem;
}

.error-message {
  color: #991b1b;
  font-size: 0.75rem;
  margin: 0;
}
</style>
